/**
 * O envio de um trabalho para a biblioteca.
 *
 * Fica fora do editor de propósito: publicar não é escrever, e sim decidir
 * divulgar. Aqui a pessoa escolhe o nível (é o nível que define o preço), declara
 * como usou IA e, se quiser, liga o pedido de contribuição.
 *
 * A declaração de uso de IA é obrigatória e não é enfeite: declarar **texto
 * gerado por IA** faz a publicação entrar sem cobrança. Texto de IA não tem
 * autoria a vender.
 */

import { useState } from 'react';
import { BookOpen, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { api, ErroApi } from '@/lib/api';
import {
  ROTULO_NIVEL_DA_BIBLIOTECA,
  ROTULO_USO_DE_IA,
  precoFormatado,
} from '@/lib/biblioteca';
import type { NivelDaBiblioteca, UsoDeIA } from '@/lib/biblioteca';
import { NIVEIS_DA_BIBLIOTECA, PRECO_POR_NIVEL_CENTAVOS, USOS_DE_IA } from '@miolos/core';

export function PublicarNaBiblioteca({
  documentoId,
  aoPublicar,
}: {
  documentoId: string;
  /** Chamado depois de enviado, para a lista recarregar. */
  aoPublicar?: () => void | Promise<void>;
}) {
  const [aberto, setAberto] = useState(false);
  const [nivel, setNivel] = useState<NivelDaBiblioteca>('GRADUACAO');
  const [usaIA, setUsaIA] = useState<UsoDeIA>('NAO');
  const [comoUsouIA, setComoUsouIA] = useState('');
  const [contribuicao, setContribuicao] = useState(false);
  const [valorSugerido, setValorSugerido] = useState('');
  const [aceitaTermo, setAceitaTermo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [enviado, setEnviado] = useState(false);

  const gratuito = usaIA === 'TOTAL';

  const enviar = async () => {
    setErro('');
    if (!aceitaTermo) {
      setErro('É preciso confirmar que o trabalho é seu e que você pode publicá-lo.');
      return;
    }
    setEnviando(true);
    try {
      const centavos = valorSugerido.trim()
        ? Math.round(Number(valorSugerido.replace(',', '.')) * 100)
        : undefined;
      await api.publicarNaBiblioteca({
        documentoId,
        nivel,
        usaIA,
        comoUsouIA: comoUsouIA.trim() || undefined,
        contribuicaoAtiva: contribuicao && !gratuito,
        contribuicaoSugeridaCentavos: Number.isFinite(centavos) ? centavos : undefined,
        aceitaTermo: true,
      });
      setEnviado(true);
      await aoPublicar?.();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível enviar para a biblioteca.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogTrigger asChild>
        <Button variant="contorno" tamanho="pequeno">
          <BookOpen />
          Publicar na biblioteca
        </Button>
      </DialogTrigger>
      <DialogContent
        titulo="Publicar na biblioteca"
        descricao="O resumo fica aberto para qualquer visitante; o texto completo abre com a consulta."
      >
        {enviado ? (
          <div className="space-y-3 text-sm leading-relaxed">
            <p className="rounded-md border border-primary/40 bg-primary/5 p-3">
              Enviado. Seu trabalho está <strong>em análise</strong> — nada aparece na biblioteca antes de o
              administrador liberar. Você acompanha o estado em <strong>Biblioteca → Meus trabalhos</strong>.
            </p>
            <Button variant="contorno" onClick={() => setAberto(false)}>
              Fechar
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Campo rotulo="Nível do trabalho" dica="É o nível que define o preço da consulta.">
              <select
                value={nivel}
                onChange={(evento) => setNivel(evento.target.value as NivelDaBiblioteca)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {NIVEIS_DA_BIBLIOTECA.map((item) => (
                  <option key={item} value={item}>
                    {ROTULO_NIVEL_DA_BIBLIOTECA[item]} — {precoFormatado(PRECO_POR_NIVEL_CENTAVOS[item])}
                  </option>
                ))}
              </select>
            </Campo>

            <Campo
              rotulo="Você usou IA para escrever?"
              dica="Texto gerado por IA entra aberto e sem cobrança — não há autoria a vender."
            >
              <select
                value={usaIA}
                onChange={(evento) => setUsaIA(evento.target.value as UsoDeIA)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {USOS_DE_IA.map((item) => (
                  <option key={item} value={item}>
                    {ROTULO_USO_DE_IA[item]}
                  </option>
                ))}
              </select>
            </Campo>

            {usaIA !== 'NAO' ? (
              <Campo rotulo="Como a IA foi usada" dica="Opcional, mas ajuda a análise.">
                <textarea
                  value={comoUsouIA}
                  onChange={(evento) => setComoUsouIA(evento.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-input bg-background p-2 text-sm"
                  placeholder="Usei para revisar a gramática do capítulo 2."
                />
              </Campo>
            ) : null}

            {gratuito ? (
              <p className="rounded-md bg-muted/60 p-2.5 text-xs leading-relaxed">
                Com a declaração de texto gerado por IA, o trabalho fica <strong>aberto e gratuito</strong>:
                o resumo aparece na biblioteca e ninguém paga para ler.
              </p>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={contribuicao}
                    onChange={(evento) => setContribuicao(evento.target.checked)}
                  />
                  Aceitar contribuição de quem consultar
                </label>
                {contribuicao ? (
                  <Campo rotulo="Valor sugerido (R$)" dica="O leitor pode mudar ou deixar em branco.">
                    <input
                      value={valorSugerido}
                      onChange={(evento) => setValorSugerido(evento.target.value)}
                      inputMode="decimal"
                      placeholder="10,00"
                      className="h-9 w-32 rounded-md border border-input bg-background px-3 text-sm"
                    />
                  </Campo>
                ) : null}
              </>
            )}

            <label className="flex items-start gap-2 rounded-md border border-border p-2.5 text-xs leading-relaxed">
              <input
                type="checkbox"
                checked={aceitaTermo}
                onChange={(evento) => setAceitaTermo(evento.target.checked)}
                className="mt-0.5"
              />
              <span>
                Declaro que escrevi este trabalho, que tenho o direito de publicá-lo e que ele não infringe
                direitos de terceiros. Sei que a instituição pode ter regras próprias sobre divulgação.
              </span>
            </label>

            {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

            <Button onClick={() => void enviar()} disabled={enviando || !aceitaTermo}>
              {enviando ? <Loader2 className="animate-spin" /> : <Send />}
              Enviar para análise
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
